import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import Container from "@material-ui/core/Container";
import Button from "@material-ui/core/Button";
import Nav from "./Nav";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import PersonPinIcon from "@material-ui/icons/PersonPin";
import Tooltip from "@material-ui/core/Tooltip";
import PropTypes from "prop-types";
import AppBar from "@material-ui/core/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import SwipeableViews from "react-swipeable-views";
import Slide from "@material-ui/core/Slide";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Link } from "react-router-dom";
import TablePagination from "@material-ui/core/TablePagination";
import { UserContext } from "./UserContext";

const useStyles = makeStyles((theme) => ({
  appBar: { backgroundColor: "black" },
  searchField: { width: "100%", marginBottom: theme.spacing(2) },
  sentinel: { height: 1 },
  loadingRow: { textAlign: "center", padding: theme.spacing(2) },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}><Typography component="div">{children}</Typography></Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return { id: `simple-tab-${index}`, "aria-controls": `simple-tabpanel-${index}` };
}

const TABLE_HEADERS = (
  <TableRow>
    <TableCell><strong>DiscordID</strong></TableCell>
    <TableCell align="right"><strong>SteamID</strong></TableCell>
    <TableCell align="right"><strong>Staff Handler</strong></TableCell>
    <TableCell align="right"><strong>Status</strong></TableCell>
    <TableCell align="right"><strong>Reason</strong></TableCell>
    <TableCell align="center"><strong>Action</strong></TableCell>
  </TableRow>
);

function AppRow({ application, fallbackReason }) {
  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <TableRow key={application._id}>
        <TableCell>{application.discordID.substring(0, 80)}</TableCell>
        <TableCell align="right">{application.steamID.substring(0, 80)}</TableCell>
        <TableCell align="right">
          {application.handler?.name ?? "System"}
        </TableCell>
        <TableCell align="right">{application.status}</TableCell>
        <TableCell align="right">{fallbackReason ?? application.reason}</TableCell>
        <TableCell align="center">
          <Tooltip title="Details">
            <IconButton aria-label="details" component={Link} to={`/applications/${application._id}`}>
              <PersonPinIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    </Slide>
  );
}

export default function DataTable() {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(UserContext);

  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const [pendingApplications, setPendingApplications] = useState([]);
  const [acceptedApplications, setAcceptedApplications] = useState([]);
  const [deniedApplications, setDeniedApplications] = useState([]);

  const [cursors, setCursors] = useState({ accepted: null, denied: null });
  const [hasMore, setHasMore] = useState({ accepted: false, denied: false });
  const [isLoading, setIsLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const searchDebounce = useRef(null);

  const acceptedSentinelRef = useRef(null);
  const deniedSentinelRef = useRef(null);

  const fetchPending = () => {
    fetch(`${process.env.REACT_APP_API_URL}/api/applications`, { credentials: "include" })
      .then((r) => r.json())
      .then((json) => setPendingApplications(json));
  };

  const fetchPaged = useCallback((status, before, append, search) => {
    if (isLoading) return;
    setIsLoading(true);
    const path = status === 1 ? "/accepted" : "/denied";
    const params = new URLSearchParams();
    if (before) params.set("before", before);
    if (search) params.set("search", search);
    const qs = params.toString();
    const url = `${process.env.REACT_APP_API_URL}/api/applications${path}${qs ? `?${qs}` : ""}`;

    fetch(url, { credentials: "include" })
      .then((r) => r.json())
      .then(({ applications: apps, hasMore: more }) => {
        const key = status === 1 ? "accepted" : "denied";
        const setter = status === 1 ? setAcceptedApplications : setDeniedApplications;
        setter((prev) => (append ? [...prev, ...apps] : apps));
        setHasMore((prev) => ({ ...prev, [key]: more }));
        if (apps.length > 0) {
          setCursors((prev) => ({ ...prev, [key]: apps[apps.length - 1].sentApplicationOn }));
        }
        if (!append) setPage(0);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, [isLoading]);

  useEffect(() => {
    fetchPending();
  }, []);

  useEffect(() => {
    const makeObserver = (sentinelRef, status) => {
      const sentinel = sentinelRef.current;
      if (!sentinel) return null;
      const key = status === 1 ? "accepted" : "denied";
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && hasMore[key] && !isLoading) {
            fetchPaged(status, cursors[key], true, searchTerm);
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(sentinel);
      return observer;
    };

    const o1 = makeObserver(acceptedSentinelRef, 1);
    const o2 = makeObserver(deniedSentinelRef, 2);
    return () => {
      o1?.disconnect();
      o2?.disconnect();
    };
  }, [hasMore, cursors, isLoading, searchTerm, fetchPaged]);

  const handleTabsChange = (event, newValue) => {
    setTabValue(newValue);
    setSearchTerm("");
    setPage(0);
    if (newValue === 0) fetchPending();
    else fetchPaged(newValue, null, false, "");
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (tabValue === 0) return;
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setCursors({ accepted: null, denied: null });
      fetchPaged(tabValue, null, false, val);
    }, 400);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const filteredPending = searchTerm.trim()
    ? pendingApplications.filter((a) => {
        const s = searchTerm.trim().toLowerCase();
        return (
          a.discordID?.toLowerCase().includes(s) ||
          a.steamID?.toLowerCase().includes(s) ||
          a.characterName?.toLowerCase().includes(s)
        );
      })
    : pendingApplications;

  const currentList = tabValue === 1 ? acceptedApplications : tabValue === 2 ? deniedApplications : filteredPending;

  return (
    <div>
      <Nav />
      <br />
      <Container maxWidth="md">
        <AppBar position="static" className={classes.appBar}>
          <Tabs value={tabValue} onChange={handleTabsChange} aria-label="application tabs">
            <Tab label="Outstanding" {...a11yProps(0)} />
            <Tab label="Accepted" {...a11yProps(1)} />
            <Tab label="Denied" {...a11yProps(2)} />
          </Tabs>
        </AppBar>
      </Container>
      <Container maxWidth="md">
        <br />
        <SwipeableViews
          axis={theme.direction === "rtl" ? "x-reverse" : "x"}
          index={tabValue}
          onChangeIndex={(i) => { setTabValue(i); handleTabsChange(null, i); }}
        >
          {/* PENDING TAB */}
          <TabPanel value={tabValue} index={0}>
            <TextField
              className={classes.searchField}
              label="Search by SteamID, DiscordID or Character Name"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <TableContainer component={Paper} style={{ overflowY: "hidden" }}>
              <div style={{ width: "auto", overflowX: "scroll" }}>
                <Table aria-label="pending applications">
                  <TableHead>{TABLE_HEADERS}</TableHead>
                  <TableBody>
                    {pendingApplications.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((app) => (
                      <AppRow key={app._id} application={app} fallbackReason="Pending" />
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[50, 100, 200]}
              component="div"
              count={pendingApplications.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </TabPanel>

          {/* ACCEPTED TAB */}
          <TabPanel value={tabValue} index={1}>
            <TextField
              className={classes.searchField}
              label="Search by SteamID, DiscordID or Character Name"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <TableContainer component={Paper} style={{ overflowY: "hidden" }}>
              <div style={{ width: "auto", overflowX: "scroll" }}>
                <Table aria-label="accepted applications">
                  <TableHead>{TABLE_HEADERS}</TableHead>
                  <TableBody>
                    {acceptedApplications.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((app) => (
                      <AppRow key={app._id} application={app} />
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[50, 100, 200]}
              component="div"
              count={acceptedApplications.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
            <div ref={acceptedSentinelRef} className={classes.sentinel} />
            {isLoading && <div className={classes.loadingRow}><CircularProgress size={24} /></div>}
          </TabPanel>

          {/* DENIED TAB */}
          <TabPanel value={tabValue} index={2}>
            <TextField
              className={classes.searchField}
              label="Search by SteamID, DiscordID or Character Name"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <TableContainer component={Paper} style={{ overflowY: "hidden" }}>
              <div style={{ width: "auto", overflowX: "scroll" }}>
                <Table aria-label="denied applications">
                  <TableHead>{TABLE_HEADERS}</TableHead>
                  <TableBody>
                    {deniedApplications.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((app) => (
                      <AppRow key={app._id} application={app} />
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[50, 100, 200]}
              component="div"
              count={deniedApplications.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
            <div ref={deniedSentinelRef} className={classes.sentinel} />
            {isLoading && <div className={classes.loadingRow}><CircularProgress size={24} /></div>}
          </TabPanel>
        </SwipeableViews>
      </Container>
    </div>
  );
}
